/**
 * VWO-ready homepage hero background switcher.
 *
 * The image is the control. VWO can activate the optional video variation with:
 * window.HairGiversHeroVideoTest.setVariant('video');
 */

if (typeof window !== 'undefined' && typeof document !== 'undefined' && typeof HTMLElement !== 'undefined') {

const IMAGE_BACKGROUND_SELECTOR = '[data-vwo-hero-background="image"]';
const VIDEO_BACKGROUND_SELECTOR = '[data-vwo-hero-background="video"]';
const VIDEO_SELECTOR = '[data-vwo-hero-video]';
// Shared by hero-slider and hero-slider-v2, whose slides differ only in their BEM class.
const SLIDE_SELECTOR = '.swiper-slide';
const VIDEO_VARIANT = 'video';
const IMAGE_VARIANT = 'image';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const normalizeVariant = (value) => value === VIDEO_VARIANT ? VIDEO_VARIANT : IMAGE_VARIANT;
const hasPendingVariant = (value) => value !== undefined && value !== null;

const getVideoSources = (video) => Array.from(video.querySelectorAll('source[data-src], source[src]'));

const hasVideoSources = (video) => {
  if (!video || !video.querySelectorAll) return false;

  return getVideoSources(video).some((source) => {
    return source.dataset.src || source.getAttribute('src');
  });
};

const prefersReducedMotion = () => {
  return typeof window.matchMedia === 'function'
    && window.matchMedia(REDUCED_MOTION_QUERY).matches;
};

const getVideoPoster = (video) => {
  const isMobile = typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 767px)').matches;

  return isMobile
    ? video.dataset.vwoHeroPosterMobile || video.dataset.vwoHeroPosterDesktop
    : video.dataset.vwoHeroPosterDesktop || video.dataset.vwoHeroPosterMobile;
};

const api = window.HairGiversHeroVideoTest || {};
const pendingVariant = hasPendingVariant(api._pendingVariant)
  ? normalizeVariant(api._pendingVariant)
  : IMAGE_VARIANT;

const state = {
  requestedVariant: pendingVariant,
  hasExplicitVariant: hasPendingVariant(api._pendingVariant),
  instances: new Set(),
};

class HeroVideoTest extends HTMLElement {
  constructor() {
    super();

    this.observer = null;
    this.appliedVariant = null;
    this.boundVideos = new WeakSet();
    this.loadedVideos = new WeakSet();
    this.handleVideoError = this.handleVideoError.bind(this);
  }

  connectedCallback() {
    if (!state.hasExplicitVariant && this.dataset.vwoHeroVariant) {
      state.requestedVariant = normalizeVariant(this.dataset.vwoHeroVariant);
      state.hasExplicitVariant = true;
    }

    state.instances.add(this);
    this.bindVideoEvents();
    this.observe();
    this.refresh();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    state.instances.delete(this);
  }

  bindVideoEvents() {
    this.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      // Swiper clones nodes after the first initialization. Remove a copied
      // source URL from an unvisited clone so it remains lazy-loaded.
      if (!this.loadedVideos.has(video)) {
        getVideoSources(video).forEach((source) => {
          if (source.dataset.src) source.removeAttribute('src');
        });
      }

      if (this.boundVideos.has(video)) return;

      this.boundVideos.add(video);
      video.addEventListener('error', this.handleVideoError);
    });
  }

  observe() {
    if (this.observer || typeof MutationObserver === 'undefined') return;

    this.observer = new MutationObserver((mutations) => {
      this.bindVideoEvents();

      const variantMutation = mutations.some((mutation) => {
        return mutation.type === 'attributes' && mutation.attributeName === 'data-vwo-hero-variant';
      });
      let resetErrors = false;

      if (variantMutation) {
        const nextVariant = normalizeVariant(this.dataset.vwoHeroVariant);
        const externalVariantMutation = nextVariant !== this.appliedVariant;

        if (externalVariantMutation) {
          state.requestedVariant = nextVariant;
          state.hasExplicitVariant = true;
          resetErrors = nextVariant === VIDEO_VARIANT;
        }
      }

      this.applyVariant(state.requestedVariant, resetErrors);
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ['class', 'data-vwo-hero-variant'],
      childList: true,
      subtree: true,
    });
  }

  refresh(resetErrors = false) {
    this.bindVideoEvents();
    this.applyVariant(state.requestedVariant, resetErrors);
  }

  setVisibility(element, visible) {
    if (!element) return;

    element.hidden = !visible;
    element.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  pauseVideo(video) {
    if (!video) return;
    video.pause();
  }

  showImageFallback(slide) {
    if (!slide) return;

    const imageBackground = slide.querySelector(IMAGE_BACKGROUND_SELECTOR);
    const videoBackground = slide.querySelector(VIDEO_BACKGROUND_SELECTOR);
    const video = slide.querySelector(VIDEO_SELECTOR);

    this.pauseVideo(video);
    this.setVisibility(videoBackground, false);
    this.setVisibility(imageBackground, true);
  }

  handleVideoError(event) {
    const video = event.currentTarget;
    const slide = video.closest(SLIDE_SELECTOR);

    if (!slide) return;

    video.dataset.vwoHeroVideoError = 'true';
    this.showImageFallback(slide);
  }

  getActiveSlide() {
    const slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
    return this.querySelector('.swiper-slide-active') || slides[0];
  }

  loadVideo(video) {
    if (!video || !hasVideoSources(video)) return false;

    video.muted = true;
    video.playsInline = true;

    const poster = getVideoPoster(video);
    if (!video.poster && poster) video.poster = poster;

    const sources = getVideoSources(video);
    sources.forEach((source) => {
      if (!source.getAttribute('src') && source.dataset.src) {
        source.setAttribute('src', source.dataset.src);
      }
    });

    if (!this.loadedVideos.has(video)) {
      this.loadedVideos.add(video);
      video.load();
    }

    const handlePlayRejection = () => {
      const slide = video.closest(SLIDE_SELECTOR);

      // A play promise can reject with AbortError when the slide is changed
      // and the old video is paused. That is not a playback failure.
      if (this.appliedVariant !== VIDEO_VARIANT || this.getActiveSlide() !== slide) return;

      video.dispatchEvent(new Event('error'));
    };

    try {
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(handlePlayRejection);
    } catch (error) {
      handlePlayRejection();
    }

    return true;
  }

  syncActiveVideo() {
    if (this.appliedVariant !== VIDEO_VARIANT) return;

    const activeSlide = this.getActiveSlide();
    this.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      const slide = video.closest(SLIDE_SELECTOR);
      if (slide !== activeSlide) this.pauseVideo(video);
    });

    const activeVideo = activeSlide?.querySelector(VIDEO_SELECTOR);
    const activeVideoBackground = activeSlide?.querySelector(VIDEO_BACKGROUND_SELECTOR);

    if (!activeVideo || !activeVideoBackground || activeVideoBackground.hidden) return;

    if (!this.loadVideo(activeVideo)) this.showImageFallback(activeSlide);
  }

  applyVariant(requestedVariant, resetErrors = false) {
    const slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
    const wantsVideo = requestedVariant === VIDEO_VARIANT && !prefersReducedMotion();
    let videoIsAvailable = false;

    slides.forEach((slide) => {
      const imageBackground = slide.querySelector(IMAGE_BACKGROUND_SELECTOR);
      const videoBackground = slide.querySelector(VIDEO_BACKGROUND_SELECTOR);
      const video = slide.querySelector(VIDEO_SELECTOR);

      if (resetErrors && video) video.removeAttribute('data-vwo-hero-video-error');

      const canUseVideo = wantsVideo
        && videoBackground
        && hasVideoSources(video)
        && video.dataset.vwoHeroVideoError !== 'true';

      if (canUseVideo) {
        videoIsAvailable = true;
        this.setVisibility(imageBackground, false);
        this.setVisibility(videoBackground, true);
      } else {
        this.showImageFallback(slide);
      }
    });

    const appliedVariant = wantsVideo && videoIsAvailable ? VIDEO_VARIANT : IMAGE_VARIANT;
    this.appliedVariant = appliedVariant;

    if (this.dataset.vwoHeroVariant !== appliedVariant) {
      this.dataset.vwoHeroVariant = appliedVariant;
    }

    this.syncActiveVideo();
  }
}

const setVariant = (value) => {
  state.requestedVariant = normalizeVariant(value);
  state.hasExplicitVariant = true;
  api._pendingVariant = state.requestedVariant;
  state.instances.forEach((instance) => instance.refresh(state.requestedVariant === VIDEO_VARIANT));
  return state.requestedVariant;
};

api.setVariant = setVariant;
window.HairGiversHeroVideoTest = api;

if (typeof customElements !== 'undefined' && !customElements.get('hero-video-test')) {
  customElements.define('hero-video-test', HeroVideoTest);
}

if (typeof window.matchMedia === 'function') {
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
  const handleReducedMotionChange = () => {
    state.instances.forEach((instance) => instance.refresh());
  };

  if (reducedMotion.addEventListener) {
    reducedMotion.addEventListener('change', handleReducedMotionChange);
  } else if (reducedMotion.addListener) {
    reducedMotion.addListener(handleReducedMotionChange);
  }
}

window.addEventListener('shopify:section:load', () => {
  state.instances.forEach((instance) => instance.refresh());
});

document.dispatchEvent(new CustomEvent('hairgivers:hero-video-api-ready'));

}
